import type { IntlShape } from "@formatjs/intl";
import { Empty, Flex, Switch, Table, Tag, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import { useMemo } from "react";
import * as commonColumns from "../../../common/columns";
import { isMaterialUsageRecorded } from "../../../common/utils/serviceMaterials";
import {
  groupSuppliesByCategory,
  supplyCategoryColor,
  supplyCategoryMessageId,
} from "../../../common/utils/supplyCategories";
import { useFinishedSuppliesFilter } from "../../../common/utils/useFinishedSuppliesFilter";
import type { TicketMaterialsUsage } from "../../generated-axios-client";
import { measurementUnitLabelMap } from "../ticketConstants";
import styles from "./SuppliesInfo.module.css";

type Props = {
  intl: IntlShape;
  materials: TicketMaterialsUsage[];
  materialCatalog: Record<string, string>;
  sapCodeCatalog: Record<string, string | null>;
  categoryCatalog: Record<string, string | null>;
  ticketStatus: string | null | undefined;
};

// The table lists each category's supplies under a header row; every row of a
// category carries the category's colored line.
type SupplyRow =
  | { kind: "category"; key: string; category: string | null; count: number }
  | { kind: "supply"; key: string; category: string | null; usage: TicketMaterialsUsage };

const categoryLineStyle = (category: string | null) => ({
  boxShadow: `inset calc(var(--ant-line-width-bold) * 2) 0 0 ${supplyCategoryColor(category)}`,
});

export const SuppliesInfo = ({
  intl,
  materials,
  materialCatalog,
  sapCodeCatalog,
  categoryCatalog,
  ticketStatus,
}: Props) => {
  const { recordingClosed, visibleMaterials, unusedCount, showUnused, setShowUnused } =
    useFinishedSuppliesFilter(materials, ticketStatus);

  const rows = useMemo(
    (): SupplyRow[] =>
      groupSuppliesByCategory(visibleMaterials, categoryCatalog).flatMap(
        ({ category, supplies }) => [
          {
            kind: "category" as const,
            key: `category:${category ?? "none"}`,
            category,
            count: supplies.length,
          },
          ...supplies.map((usage) => ({
            kind: "supply" as const,
            key: usage.id ?? usage.material ?? "",
            category,
            usage,
          })),
        ]
      ),
    [visibleMaterials, categoryCatalog]
  );

  const supplyColumn = (
    column: Omit<ColumnType<SupplyRow>, "render"> & {
      render: (usage: TicketMaterialsUsage) => React.ReactNode;
    },
    columnCount: number,
    isFirst: boolean
  ): ColumnType<SupplyRow> => ({
    ...column,
    onCell: (row) => {
      if (row.kind === "category") {
        return { colSpan: isFirst ? columnCount : 0, className: styles.categoryCell };
      }
      return isFirst ? { style: categoryLineStyle(row.category) } : {};
    },
    render: (_: unknown, row) => {
      if (row.kind === "supply") return column.render(row.usage);
      if (!isFirst) return null;
      return (
        <Flex align="center" gap="small">
          <span
            className={styles.categorySwatch}
            style={{ backgroundColor: supplyCategoryColor(row.category) }}
          />
          <Typography.Text strong>
            {intl.formatMessage({ id: supplyCategoryMessageId(row.category) })}
          </Typography.Text>
          <Typography.Text type="secondary">({row.count})</Typography.Text>
        </Flex>
      );
    },
  });

  const columnDefinitions: Array<
    Omit<ColumnType<SupplyRow>, "render"> & {
      render: (usage: TicketMaterialsUsage) => React.ReactNode;
    }
  > = [
    {
      key: "material",
      title: intl.formatMessage({ id: "ticket_supplies_column_material" }),
      render: (usage) =>
        usage.material ? (materialCatalog[usage.material] ?? usage.material) : "—",
    },
    {
      key: "sap_code",
      title: intl.formatMessage({ id: "ticket_supplies_column_sap_code" }),
      render: (usage) =>
        (usage.material && sapCodeCatalog[usage.material]) ?? (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      key: "amount",
      title: intl.formatMessage({ id: "ticket_supplies_column_amount" }),
      render: (usage) => {
        if (usage.amount == null) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        const unit = usage.measurement_unit
          ? measurementUnitLabelMap[usage.measurement_unit]
          : undefined;
        return unit ? `${usage.amount} ${unit}` : usage.amount;
      },
    },
    {
      key: "batch",
      title: intl.formatMessage({ id: "ticket_supplies_column_batch" }),
      render: (usage) =>
        usage.batch != null ? (
          usage.batch
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      key: "expiration_date",
      title: intl.formatMessage({ id: "ticket_supply_expiration_date" }),
      render: (usage) =>
        usage.expiration_date ? (
          commonColumns.dateValue(usage.expiration_date, intl)
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      key: "status",
      title: intl.formatMessage({ id: "ticket_supplies_column_status" }),
      render: (usage) =>
        isMaterialUsageRecorded(usage) ? (
          <Tag color="success">
            {intl.formatMessage({ id: "ticket_supplies_status_used" })}
          </Tag>
        ) : (
          <Tag color="default">
            {intl.formatMessage({ id: "ticket_supplies_status_not_recorded" })}
          </Tag>
        ),
      width: 130,
    },
    {
      key: "created_at",
      title: intl.formatMessage({ id: "ticket_supplies_column_added" }),
      render: (usage) =>
        usage.created_at ? commonColumns.dateTimeValue(usage.created_at, intl) : "—",
      width: 160,
    },
  ];
  const columns = columnDefinitions.map((column, index) =>
    supplyColumn(column, columnDefinitions.length, index === 0)
  );

  if (materials.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "ticket_supplies_empty" })}
      />
    );
  }

  const usedCount = materials.length - unusedCount;

  return (
    <Flex vertical gap="small">
      <Flex justify="space-between" align="center" gap="small" wrap>
        <Typography.Text type="secondary">
          {intl.formatMessage(
            { id: "ticket_supplies_usage_summary" },
            { used: usedCount, total: materials.length }
          )}
        </Typography.Text>
        {recordingClosed && unusedCount > 0 && (
          <Flex gap="small" align="center">
            <Switch size="small" checked={showUnused} onChange={setShowUnused} />
            <Typography.Text>
              {intl.formatMessage(
                { id: "ticket_supplies_show_unused" },
                { count: unusedCount }
              )}
            </Typography.Text>
          </Flex>
        )}
      </Flex>
      <Table<SupplyRow>
        columns={columns}
        dataSource={rows}
        locale={{
          emptyText: intl.formatMessage({ id: "ticket_supplies_none_used" }),
        }}
        rowKey="key"
        pagination={false}
        size="small"
        scroll={{ x: "max-content" }}
      />
    </Flex>
  );
};
